import React, { useState, useEffect } from 'react';
import { X, Inbox, FolderPlus, FileText } from 'lucide-react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { MultiSelect } from './ui/MultiSelect';
import { SearchableSelect } from './ui/SearchableSelect';
import { fileRequestApi, folderApi, editorApi, authApi, teamApi, teamLeadDashboardApi } from '../lib/api';
import { FILE_REQUEST_TYPES } from '../constants/fileRequestTypes';
import { PLATFORMS } from '../constants/platforms';
import { VERTICALS } from '../constants/verticals';
import { useVerticals } from '../hooks/useVerticals';
import { useAuth } from '../contexts/AuthContext';

interface CanvasAttachment {
  file_id: string;
  filename: string;
  original_filename: string;
  file_url: string;
  file_type: string;
  file_size: number;
  thumbnail_url?: string;
}

interface DuplicateData {
  requestType?: string;
  platforms?: string[];
  verticals?: string[];
  conceptNotes?: string;
  numCreatives?: string;
  folderId?: string;
  deadline?: string;
  allowMultipleUploads?: boolean;
  requireEmail?: boolean;
  customMessage?: string;
  assignedBuyerIds?: string[];
  canvasHeadline?: string;
  canvasScript?: string;
  canvasAttachments?: CanvasAttachment[];
  sourceRequestId?: string;
}

interface CreateFileRequestModalProps {
  onClose: () => void;
  onSuccess: () => void;
  teamId?: string;
  initialData?: DuplicateData;
}

interface Folder {
  id: string;
  name: string;
}

interface Editor {
  id: string;
  name: string;
  display_name?: string;
}

interface Buyer {
  id: string;
  name: string;
  email: string;
}

interface Team {
  id: string;
  name: string;
}

interface Template {
  id: string;
  name: string;
  description: string | null;
  default_title: string | null;
  default_request_type: string | null;
  default_instructions: string | null;
  default_priority: 'low' | 'normal' | 'high' | 'urgent' | null;
  default_due_days: number | null;
  default_platform: string | null;
  default_vertical: string | null;
  default_num_creatives: number | null;
  default_folder_id: string | null;
  default_allow_multiple_uploads: boolean | null;
  default_require_email: boolean | null;
  default_custom_message: string | null;
  default_assigned_editor_ids: string[] | null;
  default_assigned_buyer_id: string | null;
  is_active?: boolean;
}

export function CreateFileRequestModal({ onClose, onSuccess, teamId, initialData }: CreateFileRequestModalProps) {
  const verticalOptions = useVerticals();
  const [requestType, setRequestType] = useState(initialData?.requestType || '');
  const [platforms, setPlatforms] = useState<string[]>(initialData?.platforms || []);
  const [verticals, setVerticals] = useState<string[]>(initialData?.verticals || []);
  const [conceptNotes, setConceptNotes] = useState(initialData?.conceptNotes || '');
  // ── ORBIT auto-fulfil: ask for the things the engine actually needs, so it never has to guess
  const [orbitEnabled, setOrbitEnabled] = useState(false);
  const [orbitScripts, setOrbitScripts] = useState('');    // "S2, S3"
  const [orbitSources, setOrbitSources] = useState('');    // "S3-TX-OLD-LADY-RIT-1.mp4"
  const [orbitState, setOrbitState] = useState('');        // "TX"
  const [orbitOffer, setOrbitOffer] = useState('');        // "$59/month"
  const [orbitDirection, setOrbitDirection] = useState('');
  const [numCreatives, setNumCreatives] = useState<string>(initialData?.numCreatives || '');
  // Video requests split the total creatives into groups by runtime: each row is
  // { count, duration(sec) }. Together they build the multi-segment duration-plan
  // string the backend prices, and the projected AI spend is confirmed before submit.
  const [durationSplits, setDurationSplits] = useState<{ count: string; duration: string }[]>([{ count: '', duration: '' }]);
  // Sum of the split counts — the creative total for a video request.
  const splitsTotal = durationSplits.reduce((sum, r) => sum + (parseInt(r.count) || 0), 0);
  const addSplit = () => { setDurationSplits(prev => [...prev, { count: '', duration: '' }]); setEstimate(null); };
  const removeSplit = (idx: number) => { setDurationSplits(prev => prev.length === 1 ? prev : prev.filter((_, i) => i !== idx)); setEstimate(null); };
  const updateSplit = (idx: number, field: 'count' | 'duration', value: string) => {
    setDurationSplits(prev => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
    setEstimate(null);
  };
  // "{c1} videos {d1} second, {c2} videos {d2} second, ..." — the multi-segment plan the backend parses.
  const buildDurationPlan = () =>
    durationSplits
      .filter(r => (parseInt(r.count) || 0) > 0 && (parseInt(r.duration) || 0) > 0)
      .map(r => `${parseInt(r.count)} videos ${parseInt(r.duration)} second`)
      .join(', ');
  // ORBIT auto-fulfil drives /regen/* endpoints, which the backend already gates with
  // requireRegenAdmin — so showing this to anyone else offers an action that would 403.
  const { user } = useAuth();
  // ORBIT auto-fulfil is ADMIN-ONLY: show it strictly to admins (by role), never any other user.
  const _addlRoles: string[] = Array.isArray((user as any)?.additional_roles) ? (user as any).additional_roles : [];
  const canUseOrbit = user?.role === 'admin' || _addlRoles.includes('admin');
  // Images and scripts have no runtime, so a per-creative duration is meaningless for them.
  // The backend derives the estimate from the creative count for these types.
  const isImageOnlyRequest = ['image', 'script'].includes((requestType || '').trim().toLowerCase());
  const [estimate, setEstimate] = useState<any>(null);
  const [estimating, setEstimating] = useState(false);
  const [showCostConfirm, setShowCostConfirm] = useState(false);
  const [folderId, setFolderId] = useState<string>(initialData?.folderId || '');
  const [deadline, setDeadline] = useState(initialData?.deadline || '');
  const [allowMultipleUploads, setAllowMultipleUploads] = useState(initialData?.allowMultipleUploads ?? true);
  const [requireEmail, setRequireEmail] = useState(initialData?.requireEmail ?? false);
  const [customMessage, setCustomMessage] = useState(initialData?.customMessage || '');
  const [selectedEditorIds, setSelectedEditorIds] = useState<string[]>([]);
  const [assignedBuyerIds, setAssignedBuyerIds] = useState<string[]>(initialData?.assignedBuyerIds || []);
  const [buyerSearch, setBuyerSearch] = useState('');
  const [folders, setFolders] = useState<Folder[]>([]);
  const [editors, setEditors] = useState<Editor[]>([]);
  const [buyers, setBuyers] = useState<Buyer[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState(teamId || '');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [showCanvas, setShowCanvas] = useState(!!(initialData?.canvasHeadline || initialData?.canvasScript || (initialData?.canvasAttachments && initialData.canvasAttachments.length > 0)));
  const [hasCanvas, setHasCanvas] = useState(false);
  const [createdRequestId, setCreatedRequestId] = useState<string | null>(null);

  // Winner variation request (framework-tree axis swap on a winning HI creative)
  const [isWinnerVariation, setIsWinnerVariation] = useState(false);
  const [winners, setWinners] = useState<Array<{ convention: string; name?: string | null; roi?: number; tier?: string | null }>>([]);
  const [winnersLoading, setWinnersLoading] = useState(false);
  const [sourceWinner, setSourceWinner] = useState('');   // convention, e.g. S2-CA-BROLL-HAM
  const [winnerManual, setWinnerManual] = useState('');    // free-text when winner not listed
  const [variedAxis, setVariedAxis] = useState<'hook' | 'script' | 'ugc' | 'broll' | ''>('');

  const isHomeInsurance = verticals.some(v => String(v).toLowerCase().includes('home insurance') || String(v).toLowerCase() === 'hi');

  // Load HI winners when the winner-variation toggle is enabled on an HI request.
  useEffect(() => {
    if (!isWinnerVariation || !isHomeInsurance) return;
    let cancelled = false;
    setWinnersLoading(true);
    teamLeadDashboardApi.getWinners()
      .then(res => { if (!cancelled) setWinners(res.data?.data?.winners || []); })
      .catch(() => { if (!cancelled) setWinners([]); })
      .finally(() => { if (!cancelled) setWinnersLoading(false); });
    return () => { cancelled = true; };
  }, [isWinnerVariation, isHomeInsurance]);

  // Preview the variation name: replace only the varied axis token in the
  // winner's convention (script-state-type-editor). Counter starts fresh at -1.
  const winnerBase = (sourceWinner || winnerManual || '').trim().toUpperCase();
  const variationPreview = (() => {
    if (!winnerBase || !variedAxis) return '';
    const m = winnerBase.match(/^(S\d{1,3})-([A-Z]{2})-([A-Z-]+?)-([A-Z]{2,5})(?:-\d+)?$/);
    if (!m) return '';
    const [, script, state, type, editor] = m;
    const swap = variedAxis === 'script' ? `${script}✱-${state}-${type}-${editor}`
      : variedAxis === 'hook' ? `${script}-${state}-${type}-${editor} (new hook)`
      : variedAxis === 'ugc' ? `${script}-${state}-${type}-✱EDITOR-1`
      : `${script}-${state}-✱TYPE-${editor}-1`;
    return swap;
  })();

  // Inline canvas brief fields
  const [canvasHeadline, setCanvasHeadline] = useState(initialData?.canvasHeadline || '');
  const [canvasScript, setCanvasScript] = useState(initialData?.canvasScript || '');
  const [canvasFiles, setCanvasFiles] = useState<File[]>([]);
  const [canvasFileInstructions, setCanvasFileInstructions] = useState<Record<number, string>>({});
  const [existingAttachments] = useState<CanvasAttachment[]>(initialData?.canvasAttachments || []);

  const fetchFolders = async () => {
    try {
      const response = await folderApi.getTree();
      setFolders(flattenFolders(response.data.data || []));
    } catch (error: any) {
      console.error('Failed to fetch folders:', error);
    }
  };

  const flattenFolders = (folders: any[], prefix = ''): Folder[] => {
    let result: Folder[] = [];
    folders.forEach((folder) => {
      const name = prefix ? `${prefix} / ${folder.name}` : folder.name;
      result.push({ id: folder.id, name });
      if (folder.children && folder.children.length > 0) {
        result = result.concat(flattenFolders(folder.children, name));
      }
    });
    return result;
  };

  const fetchEditors = async () => {
    try {
      const response = await editorApi.getAll();
      const editorsData = response.data.data || [];
      setEditors(editorsData.map((e: any) => ({
        id: e.id,
        name: e.name,
        display_name: e.display_name
      })));
    } catch (error: any) {
      console.error('Failed to fetch editors:', error);
    }
  };

  const fetchBuyers = async () => {
    try {
      const response = await authApi.getBuyers();
      const buyersData = response.data.data || [];
      setBuyers(buyersData.map((b: any) => ({
        id: b.id,
        name: b.name,
        email: b.email
      })));
    } catch (error: any) {
      console.error('Failed to fetch buyers:', error);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await teamApi.getUserTeams();
      setTeams(response.data.data || []);
    } catch (error: any) {
      console.error('Failed to fetch teams:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      // Accessible templates includes personal + team templates for teams the user belongs to
      const response = await teamApi.getAccessibleTemplates();
      const activeTemplates = (response.data.data || []).filter((t: Template) => t.is_active !== false);
      setTemplates(activeTemplates);
    } catch (error: any) {
      console.error('Failed to fetch templates:', error);
      setTemplates([]);
    }
  };

  const handleTemplateSelect = async (templateId: string) => {
    setSelectedTemplateId(templateId);

    if (!templateId) {
      // Clear form when "No team template" is selected
      setRequestType('');
      setConceptNotes('');
      setPlatforms([]);
      setVerticals([]);
      setNumCreatives('');
      setDeadline('');
      return;
    }

    const template = templates.find(t => t.id === templateId);
    if (!template) return;

    // Auto-fill ALL form fields from template
    if (template.default_title || template.default_request_type) {
      setRequestType(template.default_title || template.default_request_type || '');
    }
    if (template.default_instructions) {
      setConceptNotes(template.default_instructions);
    }
    if (template.default_platform) {
      setPlatforms([template.default_platform]);
    }
    if (template.default_vertical) {
      setVerticals([template.default_vertical]);
    }
    if (template.default_num_creatives) {
      setNumCreatives(template.default_num_creatives.toString());
    }
    if (template.default_folder_id) {
      setFolderId(template.default_folder_id);
    }
    if (template.default_allow_multiple_uploads !== undefined && template.default_allow_multiple_uploads !== null) {
      setAllowMultipleUploads(template.default_allow_multiple_uploads);
    }
    if (template.default_require_email !== undefined && template.default_require_email !== null) {
      setRequireEmail(template.default_require_email);
    }
    if (template.default_custom_message) {
      setCustomMessage(template.default_custom_message);
    }
    if (template.default_assigned_editor_ids && template.default_assigned_editor_ids.length > 0) {
      setSelectedEditorIds(template.default_assigned_editor_ids);
    }
    if (template.default_assigned_buyer_id) {
      setAssignedBuyerIds([template.default_assigned_buyer_id]);
    }
    if (template.default_due_days) {
      // Calculate deadline from default_due_days
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + template.default_due_days);
      const formattedDate = futureDate.toISOString().slice(0, 16);
      setDeadline(formattedDate);
    }
  };

  // Effects
  useEffect(() => {
    fetchFolders();
    fetchEditors();
    fetchBuyers();
    fetchTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // For video requests the splits are the source of truth: num_creatives = sum of the
  // split counts. Image/script requests keep their own manually-entered creative count.
  useEffect(() => {
    if (!isImageOnlyRequest) {
      setNumCreatives(splitsTotal > 0 ? String(splitsTotal) : '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [durationSplits, isImageOnlyRequest]);

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      setError('Folder name is required');
      return;
    }

    setCreatingFolder(true);
    setError('');

    try {
      const response = await folderApi.create({
        name: newFolderName.trim(),
        parent_folder_id: folderId || undefined
      });

      await fetchFolders();
      setFolderId(response.data.data.id);
      setNewFolderName('');
      setShowCreateFolder(false);
    } catch (error: any) {
      console.error('Failed to create folder:', error);
      setError(error.response?.data?.error || 'Failed to create folder');
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!requestType) {
      setError('Request Type is required');
      return;
    }

    if (platforms.length === 0) {
      setError('At least one platform is required');
      return;
    }

    if (verticals.length === 0) {
      setError('At least one vertical is required');
      return;
    }

    if (isImageOnlyRequest) {
      if (!numCreatives || parseInt(numCreatives) < 1) {
        setError('Number of Creatives is required and must be at least 1');
        return;
      }
    } else {
      // Video request: need at least one split with a count > 0 AND a duration > 0.
      const hasValidSplit = durationSplits.some(r => (parseInt(r.count) || 0) > 0 && (parseInt(r.duration) || 0) > 0);
      if (!hasValidSplit) {
        setError('Add at least one duration split with a count and duration greater than 0.');
        return;
      }
    }

    if (deadline) {
      const deadlineDate = new Date(deadline);
      if (deadlineDate < new Date()) {
        setError('Deadline must be a future date');
        return;
      }
    }

    // Every creator must state the per-creative duration split and accept the
    // projected AI spend before the request is submitted. First submit computes
    // the estimate and opens the confirmation dialog; the dialog calls doCreate().
    if (!showCostConfirm) {
      setEstimating(true);
      setError('');
      try {
        const res = await fileRequestApi.estimateCost(isImageOnlyRequest ? '' : buildDurationPlan(), parseInt(numCreatives) || 1, requestType);
        setEstimate(res.data?.data || null);
        setShowCostConfirm(true);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Could not calculate the estimated cost');
      } finally {
        setEstimating(false);
      }
      return;
    }

    await doCreate();
  };

  const doCreate = async () => {
    setShowCostConfirm(false);
    setCreating(true);
    setError('');

    try {
      // Get team name for folder differentiation
      const selectedTeam = selectedTeamId ? teams.find((t: any) => t.id === selectedTeamId) : null;

      const response = await fileRequestApi.create({
        title: requestType,
        description: conceptNotes.trim() || undefined,
        folder_id: folderId || undefined,
        deadline: deadline || undefined,
        allow_multiple_uploads: allowMultipleUploads,
        require_email: requireEmail,
        custom_message: customMessage.trim() || undefined,
        editor_id: selectedEditorIds[0] || undefined,
        assigned_buyer_id: assignedBuyerIds[0] || undefined,
        assigned_buyer_ids: assignedBuyerIds.length > 0 ? assignedBuyerIds : undefined,
        request_type: requestType,
        platforms: platforms,
        verticals: verticals,
        platform: platforms[0] || undefined,
        vertical: verticals[0] || undefined,
        concept_notes: conceptNotes.trim() || undefined,
        num_creatives: parseInt(numCreatives) || 1,
        duration_plan: isImageOnlyRequest ? undefined : buildDurationPlan(),
        cost_confirmed: true,
        team_name: selectedTeam ? (selectedTeam as any).name : undefined,
        is_winner_variation: isWinnerVariation || undefined,
        source_winner: isWinnerVariation ? (sourceWinner || winnerManual || undefined) : undefined,
        varied_axis: isWinnerVariation ? (variedAxis || undefined) : undefined,
        // ORBIT reads this as a WORK ORDER (scripts + source clips + counts), never as ad copy
        orbit_context: (orbitEnabled && canUseOrbit) ? [
          orbitScripts.trim() ? `Need ${numCreatives || 1} variations in ${orbitScripts.trim()}.` : '',
          ...orbitSources.split(/[,\n]/).map(f => f.trim()).filter(Boolean)
            .map(f => `Need 1 variation for this (${f}).`),
          orbitState.trim() ? `All need in ${orbitState.trim()}.` : '',
          orbitOffer.trim() ? `State the offer ${orbitOffer.trim()}.` : '',
          orbitDirection.trim(),
        ].filter(Boolean).join(' ') : undefined,
        orbit_auto: (orbitEnabled && canUseOrbit) || undefined,
      });

      // Store request ID for canvas
      const requestId = response.data?.data?.id;
      if (requestId) {
        setCreatedRequestId(requestId);

        // Assign to multiple editors if selected
        if (selectedEditorIds.length > 0) {
          await fileRequestApi.assignEditors(requestId, selectedEditorIds);
        }

        // Optionally save as personal template
        if (saveAsTemplate) {
          if (!templateName.trim()) {
            setError('Template name is required to save as template');
            return;
          }
          setSavingTemplate(true);
          try {
            await teamApi.createPersonalTemplate({
              name: templateName.trim(),
              description: `Saved from request: ${requestType}`,
              defaultTitle: requestType,
              defaultRequestType: requestType,
              defaultInstructions: conceptNotes.trim() || null,
              defaultPlatform: platforms[0] || null,
              defaultVertical: verticals[0] || null,
              defaultNumCreatives: parseInt(numCreatives) || 1,
              defaultAllowMultipleUploads: allowMultipleUploads,
              defaultRequireEmail: requireEmail,
              defaultCustomMessage: customMessage.trim() || null,
              requiredFields: []
            });
          } catch (e: any) {
            console.error('Failed to save template:', e);
            // non-fatal
          } finally {
            setSavingTemplate(false);
          }
        }

        // Copy canvas attachments from source request if duplicating
        if (initialData?.sourceRequestId && existingAttachments.length > 0) {
          try {
            await fileRequestApi.canvas.copyAttachments(requestId, initialData.sourceRequestId);
          } catch (copyErr: any) {
            console.error('Failed to copy canvas attachments:', copyErr);
          }
        }

        // Save inline canvas brief if toggled on and has content
        if (showCanvas && (canvasHeadline.trim() || canvasScript.trim() || canvasFiles.length > 0)) {
          try {
            const canvasSamples = canvasFiles.map((file, idx) => ({
              filename: file.name,
              instruction: canvasFileInstructions[idx] || '',
              type: file.type,
              size: file.size,
            }));

            const canvasContent: any = {
              headline: canvasHeadline.trim(),
              script: canvasScript.trim(),
              samples: canvasSamples,
            };
            await fileRequestApi.canvas.upsert(requestId, canvasContent);
            setHasCanvas(true);

            // Upload reference files and link file_ids back to canvas samples
            if (canvasFiles.length > 0) {
              const uploadedFileIds: string[] = [];
              for (const file of canvasFiles) {
                try {
                  const uploadRes = await fileRequestApi.canvas.uploadAttachment(requestId, file);
                  const fileId = uploadRes?.data?.attachment?.file_id;
                  uploadedFileIds.push(fileId || '');
                } catch (uploadErr: any) {
                  console.error('Failed to upload canvas attachment:', uploadErr);
                  uploadedFileIds.push('');
                }
              }

              // Update canvas content with file_ids so download buttons work
              const hasFileIds = uploadedFileIds.some(id => id);
              if (hasFileIds) {
                const updatedSamples = canvasSamples.map((sample, idx) => ({
                  ...sample,
                  file_id: uploadedFileIds[idx] || undefined,
                }));
                await fileRequestApi.canvas.upsert(requestId, {
                  ...canvasContent,
                  samples: updatedSamples,
                });
              }
            }
          } catch (canvasErr: any) {
            console.error('Failed to save canvas brief:', canvasErr);
            // Non-fatal - request was created successfully
          }
        }

        onSuccess();
      } else {
        onSuccess();
      }
    } catch (error: any) {
      console.error('Failed to create file request:', error);
      setError(error.response?.data?.error || 'Failed to create file request');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800">
          <div className="flex items-center gap-2">
            <Inbox className="w-5 h-5 text-blue-500" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {initialData ? 'Duplicate File Request' : 'Create File Request'}
            </h2>
          </div>
          <button
            onClick={() => {
              if (createdRequestId) {
                onSuccess(); // Refresh list
              }
              onClose();
            }}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Templates (Optional) */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-center justify-between gap-3 mb-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Use Template (Optional)
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={saveAsTemplate}
                  onChange={(e) => setSaveAsTemplate(e.target.checked)}
                  disabled={creating}
                />
                Save this request as template
              </label>
            </div>

            {saveAsTemplate && (
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Template name
                </label>
                <input
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="e.g. UGC 20 assets"
                  disabled={creating}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:bg-gray-700 dark:text-white"
                />
                {savingTemplate && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">Saving template…</p>
                )}
              </div>
            )}
              {/* Template Selection */}
              {templates.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select Template to Auto-Fill Form
                  </label>
                  <select
                    value={selectedTemplateId}
                    onChange={(e) => handleTemplateSelect(e.target.value)}
                    disabled={creating}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Choose a template...</option>
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                        {template.description ? ` - ${template.description}` : ''}
                      </option>
                    ))}
                  </select>
                  {selectedTemplateId && (
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                      Form fields have been auto-filled from template. You can still modify any field.
                    </p>
                  )}
                </div>
              )}

              {templates.length === 0 && (
                <p className="text-sm text-gray-600 dark:text-gray-400 italic">
                  No templates available yet.
                </p>
              )}
            </div>

          {/* Request Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Request Type *
            </label>
            <SearchableSelect
              value={requestType}
              onChange={setRequestType}
              disabled={creating}
              placeholder="Select Request Type"
              options={[
                { value: '', label: 'Select Request Type' },
                ...FILE_REQUEST_TYPES.map(type => ({ value: type, label: type }))
              ]}
            />
          </div>

          {/* Platforms - Multi-Select */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Platforms * (select multiple)
            </label>
            <MultiSelect
              options={PLATFORMS.map(p => ({ id: p, label: p }))}
              selectedIds={platforms}
              onChange={setPlatforms}
              placeholder="Select platforms..."
              disabled={creating}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {platforms.length > 0 ? `${platforms.length} platform(s) selected` : 'Select at least one platform'}
            </p>
          </div>

          {/* Verticals - Multi-Select */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Verticals * (select multiple)
            </label>
            <MultiSelect
              options={verticalOptions.map(v => ({ id: v, label: v }))}
              selectedIds={verticals}
              onChange={setVerticals}
              placeholder="Select verticals..."
              disabled={creating}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {verticals.length > 0 ? `${verticals.length} vertical(s) selected` : 'Select at least one vertical'}
            </p>
          </div>

          {/* Winner Variation Request — Home Insurance only */}
          {isHomeInsurance && (
            <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50/60 dark:bg-amber-900/10 p-3">
              <label className="flex items-center gap-2 text-sm font-medium text-amber-800 dark:text-amber-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isWinnerVariation}
                  onChange={(e) => setIsWinnerVariation(e.target.checked)}
                  disabled={creating}
                />
                🏆 Winner variation request
              </label>

              {isWinnerVariation && (
                <div className="mt-3 space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Winner creative {winnersLoading ? '(loading…)' : `(${winners.length} HI winners)`}
                    </label>
                    <select
                      value={sourceWinner}
                      onChange={(e) => setSourceWinner(e.target.value)}
                      disabled={creating}
                      className="w-full px-2 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
                    >
                      <option value="">— select a winning creative —</option>
                      {winners.map((w) => (
                        <option key={w.convention} value={w.convention}>
                          {w.convention}{w.roi != null ? ` · ROI ${Math.round(w.roi)}%` : ''}{w.tier ? ` · ${w.tier}` : ''}
                        </option>
                      ))}
                    </select>
                    <div className="mt-1">
                      <input
                        value={winnerManual}
                        onChange={(e) => setWinnerManual(e.target.value)}
                        disabled={creating || !!sourceWinner}
                        placeholder="…or type the winner name (e.g. S2-CA-BROLL-HAM-3)"
                        className="w-full px-2 py-1.5 text-xs rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 disabled:opacity-50"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Vary which axis (framework tree)
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {([['hook', 'Hook'], ['script', 'Script'], ['ugc', 'UGC / Creator'], ['broll', 'B-roll']] as const).map(([val, label]) => (
                        <button
                          key={val}
                          type="button"
                          onClick={() => setVariedAxis(val)}
                          disabled={creating}
                          className={`px-2.5 py-1 text-xs rounded-full border ${variedAxis === val
                            ? 'bg-amber-600 text-white border-amber-600'
                            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600'}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {variationPreview && (
                    <p className="text-xs text-gray-700 dark:text-gray-300">
                      New name preview: <code className="font-mono">{variationPreview}</code>
                      <span className="text-gray-400"> — only the varied token changes</span>
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Concept Notes with Canvas Toggle */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Concept Notes / Brief
              </label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowCanvas(!showCanvas)}
                disabled={creating}
                className="text-xs flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white border-blue-600"
              >
                <FileText className="w-4 h-4" />
                {showCanvas ? 'Simple Notes' : 'Canvas Brief (Detailed)'}
              </Button>
            </div>
            {!showCanvas ? (
              <textarea
                value={conceptNotes}
                onChange={(e) => setConceptNotes(e.target.value)}
                placeholder="Quick notes about this request..."
                rows={3}
                disabled={creating}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              />
            ) : (
              <div className="space-y-3 border border-blue-200 dark:border-blue-800 rounded-lg p-4 bg-blue-50/50 dark:bg-blue-900/10">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Headline
                  </label>
                  <input
                    value={canvasHeadline}
                    onChange={(e) => setCanvasHeadline(e.target.value)}
                    placeholder="Brief headline or title..."
                    disabled={creating}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Script / Description
                  </label>
                  <textarea
                    value={canvasScript}
                    onChange={(e) => setCanvasScript(e.target.value)}
                    placeholder="Detailed brief, script, or instructions..."
                    rows={4}
                    disabled={creating}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  {/* Existing attachments from duplicated request */}
                  {existingAttachments.length > 0 && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Attachments from Original Request ({existingAttachments.length} files will be copied)
                      </label>
                      <div className="space-y-1">
                        {existingAttachments.map((att, idx) => (
                          <div key={idx} className="flex items-center gap-2 bg-green-50 dark:bg-green-900/20 rounded p-2 border border-green-200 dark:border-green-800">
                            <span className="text-xs text-green-800 dark:text-green-200 flex-1 truncate">
                              {att.original_filename || att.filename}
                            </span>
                            <span className="text-xs text-green-600 dark:text-green-400">
                              {att.file_size ? `${(att.file_size / 1024).toFixed(0)} KB` : ''}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Reference Samples (optional)
                  </label>
                  <input
                    type="file"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      setCanvasFiles(prev => [...prev, ...files]);
                    }}
                    disabled={creating}
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900/30 dark:file:text-blue-300"
                  />
                  {canvasFiles.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {canvasFiles.map((file, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-white dark:bg-gray-800 rounded p-2 border border-gray-200 dark:border-gray-700">
                          <span className="text-xs text-gray-900 dark:text-white flex-1 truncate">{file.name}</span>
                          <input
                            value={canvasFileInstructions[idx] || ''}
                            onChange={(e) => setCanvasFileInstructions(prev => ({ ...prev, [idx]: e.target.value }))}
                            placeholder="Instruction for this file..."
                            className="flex-1 text-xs px-2 py-1 border border-gray-200 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setCanvasFiles(prev => prev.filter((_, i) => i !== idx));
                              setCanvasFileInstructions(prev => {
                                const next = { ...prev };
                                delete next[idx];
                                return next;
                              });
                            }}
                            className="text-red-500 hover:text-red-700 text-xs"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── ORBIT auto-fulfil ─────────────────────────────────────────────────────────────
              Ask for what the engine actually needs (scripts, source clips, state, offer) so it
              reads a WORK ORDER instead of guessing from prose. */}
          <div className={`rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50/50 dark:bg-violet-900/10 p-4 space-y-3 ${canUseOrbit ? '' : 'hidden'}`}>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={orbitEnabled} disabled={creating || !canUseOrbit}
                onChange={(e) => setOrbitEnabled(e.target.checked)} />
              <span className="text-sm font-medium text-violet-800 dark:text-violet-300">
                Let ORBIT generate these creatives
              </span>
              <span className="text-xs text-gray-500">— delivered into this request, credited to ORBIT (never an editor)</span>
            </label>

            {orbitEnabled && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Script(s) <span className="font-normal text-gray-400">— e.g. S2</span>
                  </label>
                  <input value={orbitScripts} onChange={(e) => setOrbitScripts(e.target.value)}
                    placeholder="S2" disabled={creating}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    State <span className="font-normal text-gray-400">— e.g. TX</span>
                  </label>
                  <input value={orbitState} onChange={(e) => setOrbitState(e.target.value)}
                    placeholder="Texas" disabled={creating}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Source creative(s) to make variations of
                    <span className="font-normal text-gray-400"> — exact filenames, comma separated</span>
                  </label>
                  <input value={orbitSources} onChange={(e) => setOrbitSources(e.target.value)}
                    placeholder="S3-TX-OLD-LADY-RIT-1.mp4, S4-TX-UGC-TEXT-NAN.mp4" disabled={creating}
                    className="w-full px-3 py-2 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white" />
                  <p className="text-[11px] text-gray-500 mt-1">
                    ORBIT re-uses that exact footage and reads the script/state/character from the filename.
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Offer / value <span className="font-normal text-gray-400">— stated in the ad</span>
                  </label>
                  <input value={orbitOffer} onChange={(e) => setOrbitOffer(e.target.value)}
                    placeholder="$59/month" disabled={creating}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Extra direction <span className="font-normal text-gray-400">— angle, tone</span>
                  </label>
                  <input value={orbitDirection} onChange={(e) => setOrbitDirection(e.target.value)}
                    placeholder="urgent, lead with the savings" disabled={creating}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white" />
                </div>
              </div>
            )}
          </div>

          {/* Number of Creatives — image/script only. Video requests derive the count
              from the duration splits below. */}
          {isImageOnlyRequest && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Number of Creatives *
              </label>
              <input
                type="number"
                value={numCreatives}
                onChange={(e) => setNumCreatives(e.target.value)}
                placeholder="Enter number (e.g., 5)"
                disabled={creating}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
          )}

          {/* Creatives & Duration splits — video requests. A repeatable list of
              { count, duration } rows; their sum is the total creative count and
              drives the multi-segment duration-plan the backend prices. */}
          {!isImageOnlyRequest && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Creatives &amp; Duration *
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                Split the creatives into groups by video length — each row is how many videos at that duration.
              </p>
              <div className="space-y-2">
                {durationSplits.map((row, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={row.count}
                      onChange={(e) => updateSplit(idx, 'count', e.target.value)}
                      placeholder="Count"
                      disabled={creating}
                      className="flex-1 min-w-0 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                    />
                    <span className="text-xs text-gray-400 whitespace-nowrap">videos ×</span>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={row.duration}
                      onChange={(e) => updateSplit(idx, 'duration', e.target.value)}
                      placeholder="Duration (sec)"
                      disabled={creating}
                      className="flex-1 min-w-0 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                    />
                    <span className="text-xs text-gray-400 whitespace-nowrap">sec</span>
                    <button
                      type="button"
                      onClick={() => removeSplit(idx)}
                      disabled={creating || durationSplits.length === 1}
                      title="Remove this split"
                      className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 disabled:opacity-30 disabled:hover:text-gray-400"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={addSplit}
                disabled={creating}
                className="mt-2 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
              >
                + Add duration split
              </button>
              {(() => {
                // Rate card: per row = count × (duration × 0.059 + 0.02); summed over all rows.
                const cost = durationSplits.reduce((sum, r) => {
                  const c = parseInt(r.count) || 0;
                  const d = parseInt(r.duration) || 0;
                  return sum + (c > 0 && d > 0 ? c * (d * 0.059 + 0.02) : 0);
                }, 0);
                return (
                  <div className="mt-2 flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
                    <span>Total: {splitsTotal} creative{splitsTotal === 1 ? '' : 's'}</span>
                    <span>{cost > 0 ? `Estimated AI cost $${cost.toFixed(2)}` : 'Enter a count and duration'}</span>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Destination Folder */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Destination Folder
            </label>
            <div className="flex gap-2">
              <SearchableSelect
                value={folderId}
                onChange={setFolderId}
                disabled={creating}
                placeholder="Root / No folder"
                className="flex-1"
                options={[
                  { value: '', label: 'Root / No folder' },
                  ...folders.map(f => ({ value: f.id, label: f.name }))
                ]}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateFolder(!showCreateFolder)}
                disabled={creating}
                className="flex-shrink-0"
              >
                <FolderPlus className="w-4 h-4" />
              </Button>
            </div>

            {/* Inline folder creation */}
            {showCreateFolder && (
              <div className="mt-2 flex gap-2">
                <Input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="New folder name"
                  disabled={creatingFolder}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleCreateFolder();
                    }
                  }}
                />
                <Button
                  type="button"
                  onClick={handleCreateFolder}
                  disabled={creatingFolder || !newFolderName.trim()}
                  size="sm"
                >
                  {creatingFolder ? 'Creating...' : 'Create'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowCreateFolder(false);
                    setNewFolderName('');
                  }}
                  disabled={creatingFolder}
                  size="sm"
                >
                  Cancel
                </Button>
              </div>
            )}

            <p className="text-xs text-muted-foreground mt-1">
              Uploaded files will be saved to this folder
            </p>
          </div>

          {/* Editor Assignment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Assign to Editors (optional)
            </label>
            <MultiSelect
              options={editors.map(e => ({
                id: e.id,
                label: e.display_name || e.name
              }))}
              selectedIds={selectedEditorIds}
              onChange={setSelectedEditorIds}
              placeholder="Select editors..."
              disabled={creating}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {verticals.length > 0 ? 'Auto-assigned to vertical head(s). You can select additional editors here.' : 'Search and select multiple editors easily'}
            </p>
          </div>

          {/* Vertical Auto-Assignment Notice */}
          {verticals.length > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <p className="text-sm text-blue-900 dark:text-blue-200 font-medium">
                Vertical-Based Assignment Active
              </p>
              <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                This request will be automatically assigned to the vertical head(s) for: {verticals.join(', ')}.
              </p>
            </div>
          )}

          {/* Buyer Assignment - Always visible */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Assign to Buyer(s) (optional)
            </label>
            {buyers.length === 0 ? (
              <p className="text-xs text-gray-500 dark:text-gray-400 italic">No buyers available</p>
            ) : (
              <div>
                <input
                  type="text"
                  placeholder="Search buyers by name or email..."
                  value={buyerSearch}
                  onChange={(e) => setBuyerSearch(e.target.value)}
                  className="w-full px-3 py-1.5 mb-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
                <div className="max-h-40 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 p-2 space-y-1">
                  {buyers.filter((b) => {
                    if (!buyerSearch.trim()) return true;
                    const s = buyerSearch.toLowerCase();
                    return b.name.toLowerCase().includes(s) || b.email.toLowerCase().includes(s);
                  }).map((buyer) => (
                    <label
                      key={buyer.id}
                      className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={assignedBuyerIds.includes(buyer.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setAssignedBuyerIds(prev => [...prev, buyer.id]);
                          } else {
                            setAssignedBuyerIds(prev => prev.filter(id => id !== buyer.id));
                          }
                        }}
                        disabled={creating}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-900 dark:text-white">{buyer.name}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">({buyer.email})</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {assignedBuyerIds.length > 0 && (
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                {assignedBuyerIds.length} buyer(s) selected - uploaded files will be accessible to them
              </p>
            )}
          </div>

          {/* Assign to Team */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Assign to Team (optional)
            </label>
            {teams.length === 0 ? (
              <p className="text-xs text-gray-500 dark:text-gray-400 italic">No teams available</p>
            ) : (
              <select
                value={selectedTeamId}
                onChange={async (e) => {
                  const teamId = e.target.value;
                  setSelectedTeamId(teamId);
                  setSelectedTemplateId('');

                  // Auto-populate assigned buyers with all team members
                  if (teamId) {
                    try {
                      const teamResponse = await teamApi.getTeam(teamId);
                      const teamData = teamResponse.data?.data || teamResponse.data;
                      const members = teamData?.members || [];
                      // Add all team member user IDs to assigned buyers (deduplicated)
                      const memberUserIds: string[] = members.map((m: any) => m.user_id).filter(Boolean);
                      setAssignedBuyerIds(prev => {
                        const combined = new Set([...prev, ...memberUserIds]);
                        return Array.from(combined);
                      });
                    } catch (err) {
                      console.error('Failed to fetch team members:', err);
                    }
                  }
                }}
                disabled={creating}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="">No team (individual request)</option>
                {teams.map((team: any) => (
                  <option key={team.id} value={team.id}>
                    {team.name} ({team.member_count || 0} members)
                  </option>
                ))}
              </select>
            )}
            {selectedTeamId && (
              <p className="text-xs text-blue-600 mt-1">
                All team members have been added as assigned buyers
              </p>
            )}
          </div>

          {/* Deadline */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Deadline (optional)
            </label>
            <Input
              type="datetime-local"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              disabled={creating}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Request will not accept uploads after this date
            </p>
          </div>

          {/* Custom Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Custom Message (optional)
            </label>
            <textarea
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="Add a custom message for uploaders..."
              rows={2}
              disabled={creating}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* Options */}
          <div className="space-y-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={allowMultipleUploads}
                onChange={(e) => setAllowMultipleUploads(e.target.checked)}
                disabled={creating}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Allow multiple file uploads
              </span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={requireEmail}
                onChange={(e) => setRequireEmail(e.target.checked)}
                disabled={creating}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Require uploader email
              </span>
            </label>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                if (createdRequestId) {
                  onSuccess(); // Refresh list
                }
                onClose();
              }}
              disabled={creating}
            >
              {createdRequestId ? 'Done' : 'Cancel'}
            </Button>
            {!createdRequestId && (
              <Button
                type="submit"
                disabled={creating || estimating || !requestType || platforms.length === 0 || verticals.length === 0}
              >
                {creating ? 'Creating...' : estimating ? 'Calculating cost...' : 'Review Cost & Submit'}
              </Button>
            )}
          </div>
        </form>
      </div>

      {/* Cost confirmation — the real projected AI spend, shown before anything is submitted */}
      {showCostConfirm && estimate && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Estimated Cost</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Projected AI generation spend (Seedance 480p). Excludes editor time.
              </p>
            </div>

            <div className="px-5 py-4 space-y-3">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-1.5">Creatives</th>
                    <th className="text-left py-1.5">Length</th>
                    <th className="text-right py-1.5">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {estimate.estimate.lines.map((line: any, i: number) => (
                    <tr key={i} className="border-b border-gray-100 dark:border-gray-700/50">
                      <td className="py-2 text-gray-900 dark:text-white">{line.count}</td>
                      <td className="py-2 text-gray-600 dark:text-gray-400">
                        {line.media_type === 'image' ? 'image' : `${line.seconds}s video`}
                        <span className="block text-[10px] text-gray-400">{line.detail}</span>
                      </td>
                      <td className="py-2 text-right font-medium text-gray-900 dark:text-white">
                        ${line.subtotal_usd.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="flex justify-between items-baseline pt-1">
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Total &middot; {estimate.estimate.total_creatives} creatives
                  {estimate.estimate.total_video_seconds > 0 && ` · ${estimate.estimate.total_video_seconds}s of video`}
                </span>
                <span className="text-2xl font-bold text-gray-900 dark:text-white">
                  ${estimate.estimate.total_usd.toFixed(2)}
                </span>
              </div>

              <p className="text-[11px] text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 pt-2">
                Rate applied: ${estimate.estimate.rate_card.applied_per_sec}/second of generated video,
                plus ${estimate.estimate.rate_card.per_video_overhead} per creative for script, voice and captions.
                This is an estimate — actual spend is recorded by editors on delivery.
              </p>

              <p className="text-sm font-medium text-gray-900 dark:text-white pt-1">
                Is this your final call?
              </p>
            </div>

            <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCostConfirm(false)} disabled={creating}>
                Go Back &amp; Edit
              </Button>
              <Button onClick={doCreate} disabled={creating}>
                {creating ? 'Creating...' : `Confirm & Submit ($${estimate.estimate.total_usd.toFixed(2)})`}
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
